import { useMemo, useState } from "react";
import { Box, Typography, useTheme, Skeleton, IconButton, Tooltip } from "@mui/material";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import EvidenceTableContainer from "../../common/EvidenceTableContainer";
import CheckActivityGraph from "../CheckActivityGraph";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EvidenceLoadingSkeleton from "../../common/EvidenceLoadingSkeleton";
import StatusFilterPopover from "../../common/StatusFilterPopover";
import ReusableTableSkeleton from "../../../../../components/ReusableTable/TableSkeleton";
import DatePicker from "../../../../../components/DatePicker/DatePicker";

const InfraEvidenceTabBase = ({
  resourcesData,
  mapResource,
  columns,
  renderCell,
  loading,
  activeCheckId,
  checkCategory,
  rightAction,
  selectedDate,
  onDateChange,
  auditStartDate,
  auditEndDate,
  defaultStatusFilter = [],
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter);
  const [showActivity, setShowActivity] = useState(false);

  const resourcesWithEvidence = useMemo(() => {
    if (!resourcesData || resourcesData.length === 0) return [];
    return mapResource(resourcesData);
  }, [resourcesData, mapResource]);

  const filteredResources = useMemo(() => {
    let filtered = resourcesWithEvidence;

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item?.name?.toLowerCase().includes(query) ||
          (item?.resourceId && item.resourceId.toString().toLowerCase().includes(query)) ||
          (item?.account && item.account?.toLowerCase().includes(query))
      );
    }

    // Apply status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter((item) =>
        statusFilter.includes((item.status || "").toLowerCase())
      );
    }

    return filtered;
  }, [resourcesWithEvidence, searchTerm, statusFilter]);

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter([]);
  };

  const handleDateChange = (newDate) => {
    if (newDate && newDate !== selectedDate) {
      onDateChange?.(newDate);
    }
  };

  // Add StatusFilterPopover to Status column if it exists
  const columnsWithStatusFilter = useMemo(() => {
    return columns.map((col) => {
      if (col.field === "status") {
        return {
          ...col,
          label: (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              Status
              <StatusFilterPopover
                onFilterChange={setStatusFilter}
                activeFilters={statusFilter}
              />
            </Box>
          ),
        };
      }
      return col;
    });
  }, [columns, statusFilter]);

  // Date picker component - extracted, styled pill with blue shading
  const datePickerComponent = useMemo(() => {
    if (!auditStartDate || !auditEndDate) return null;
    return (
      <DatePicker
        minDate={auditStartDate}
        maxDate={auditEndDate}
        value={selectedDate}
        onChange={handleDateChange}
      />
    );
  }, [auditStartDate, auditEndDate, selectedDate, onDateChange]);

  // For initial load (no date selected yet), show full-page skeleton.
  // For subsequent date changes, keep the layout and only skeleton the table.
  if (loading && !selectedDate) {
    return (
      <EvidenceLoadingSkeleton/>
    );
  }

  // If showing activity graph, replace entire view
  if (showActivity && activeCheckId) {
    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <CheckActivityGraph
          activeCheckId={activeCheckId}
          checkCategory={checkCategory}
          rightAction={
            <Tooltip title="Back to Resources" placement="left">
              <IconButton
                onClick={() => setShowActivity(false)}
                size="small"
                sx={{
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.palette.grey[200],
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          }
        />
      </Box>
    );
  }

  // Default: Show Resources with slide button (NO collapse button)
  return (
    <Box sx={{ width: "100%" }}>
      <EvidenceTableContainer
        title="Resources"
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        clearFilters={clearFilters}
        filteredCount={filteredResources.length}
        countLabel={`${filteredResources.length} ${
          filteredResources.length === 1 ? "resource" : "resources"
        } found`}
        tableExpanded={true}
        onToggleExpand={() => setShowActivity(true)}
        showExpandCollapse={true}
        leftAction={datePickerComponent}
        rightAction={rightAction}
      >
        <Box sx={{ width: "100%", maxHeight: "100%" }}>
          {loading && selectedDate ? (
            <ReusableTableSkeleton columns={columnsWithStatusFilter} skeletonRows={8} maxheight="500px" />
          ) : (
          <ReusableTable
            columns={columnsWithStatusFilter}
            data={filteredResources}
            renderCell={renderCell}
            emptyMessage="No resources match your search"
            stickyHeader
            maxHeight="500px"
          />
          )}
        </Box>
      </EvidenceTableContainer>
    </Box>
  );
};

export default InfraEvidenceTabBase;
